import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Button, DatePicker, Modal, Select, Space, Table, Tag, message} from 'antd';
import {DownloadOutlined} from '@ant-design/icons';
import {exportPushHistory, getPushHistory} from '@/api/hrPush';
import HistoryDetailModal from './HistoryDetailModal';
import {formatApiTime} from '../utils';

export default function PushHistoryModal({appId, task, onClose}) {
    const [form, setForm] = useState({});
    const [rows, setRows] = useState([]);
    const [pagination, setPagination] = useState({current: 1, size: 10, total: 0});
    const [detailId, setDetailId] = useState(null);

    const load = async (current = 1) => {
        const {data} = await getPushHistory(appId, task.id, {
            current,
            size: pagination.size,
            ...form,
            start: form.start?.format('YYYY-MM-DD'),
            end: form.end?.format('YYYY-MM-DD'),
        });
        setRows(data?.records || []);
        setPagination({...pagination, current, total: data?.total || 0});
    };

    useEffect(() => {
        if (task) load(1);
    }, [task]);

    const exportExcel = async () => {
        try {
            const blob = await exportPushHistory(appId, task.id, {
                ...form,
                start: form.start?.format('YYYY-MM-DD'),
                end: form.end?.format('YYYY-MM-DD'),
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `推送日志-${task.taskName || task.id}.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            setTimeout(() => URL.revokeObjectURL(url), 0);
        } catch (e) {
            message.error(e?.message || '导出失败');
        }
    };

    const columns = [
        {title: '任务名称', dataIndex: 'taskId', render: () => task.taskName},
        {title: '消息发送人数', dataIndex: 'targetCount', width: 120},
        {
            title: '执行状态',
            dataIndex: 'status',
            width: 110,
            render: value => (
                <Tag color={value === 2 ? 'green' : value === 4 ? 'red' : 'orange'}>
                    {value === 2 ? '● 成功' : value === 4 ? '● 失败' : '● 部分失败'}
                </Tag>
            ),
        },
        {
            title: '开始时间',
            dataIndex: 'executeTime',
            width: 170,
            render: formatApiTime,
        },
        {
            title: '耗时',
            dataIndex: 'durationMs',
            width: 120,
            render: value => (value ? `${Math.floor(value / 1000)}s` : '-'),
        },
        {
            title: '操作',
            width: 80,
            render: (_, row) => (
                <Button type="link" onClick={() => setDetailId(row.id)}>
                    详情
                </Button>
            ),
        },
    ];

    return (
        <Modal open={!!task} title="推送日志" footer={null} width={900} onCancel={onClose}>
            <Space wrap style={{marginBottom: 16}}>
                <Select
                    placeholder="状态"
                    allowClear
                    style={{width: 120}}
                    onChange={value => setForm(old => ({...old, status: value}))}
                    options={[
                        {value: 2, label: '成功'},
                        {value: 3, label: '部分失败'},
                        {value: 4, label: '失败'},
                    ]}
                />
                <DatePicker
                    placeholder="开始时间"
                    onChange={value => setForm(old => ({...old, start: value}))}
                />
                <DatePicker
                    placeholder="截止时间"
                    onChange={value => setForm(old => ({...old, end: value}))}
                />
                <Button type="primary" onClick={() => load(1)}>
                    查询
                </Button>
                <Button
                    onClick={() => {
                        setForm({});
                        load(1);
                    }}
                >
                    重置
                </Button>
                <Button icon={<DownloadOutlined />} type="primary" onClick={exportExcel}>
                    导出
                </Button>
            </Space>
            <Table
                rowKey="id"
                columns={columns}
                dataSource={rows}
                locale={{
                    emptyText:
                        task?.pushDate && !task?.lastExecuteTime
                            ? '任务尚未执行，暂无推送日志'
                            : '暂无推送日志',
                }}
                pagination={{
                    current: pagination.current,
                    total: pagination.total,
                    pageSize: pagination.size,
                    showTotal: total => `共 ${total} 条记录`,
                    onChange: load,
                }}
            />
            {detailId && (
                <HistoryDetailModal
                    appId={appId}
                    historyId={detailId}
                    taskName={task.taskName}
                    onClose={() => setDetailId(null)}
                />
            )}
        </Modal>
    );
}

PushHistoryModal.propTypes = {
    appId: PropTypes.string.isRequired,
    task: PropTypes.shape({
        id: PropTypes.string,
        taskName: PropTypes.string,
        pushDate: PropTypes.string,
        lastExecuteTime: PropTypes.string,
    }),
    onClose: PropTypes.func.isRequired,
};

PushHistoryModal.defaultProps = {
    task: null,
};
